/**
 * Admin Driver Payments Component
 * Shows all drivers with earnings summary and payment management
 * R30 per completed delivery
 * 
 * Payment Flow: unpaid → approved → paid
 * - unpaid: Delivery completed, earning recorded
 * - approved: Admin authorizes payment (queued for EFT)
 * - paid: Actual money transferred (EFT done)
 */

import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const EARNING_PER_DELIVERY = 30;

const AdminDriverPayments = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(null);
  
  const [driverFilter, setDriverFilter] = useState('active');
  const [dateFilter, setDateFilter] = useState('this_month');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const [expandedDriver, setExpandedDriver] = useState(null);
  const [driverDeliveries, setDriverDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);

  useEffect(() => {
    fetchDriversWithEarnings();
  }, [driverFilter, dateFilter, customStartDate, customEndDate, paymentStatusFilter]);

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        endDate = new Date();
        break;
      case 'yesterday':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'this_week':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - startDate.getDay());
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'last_week':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - startDate.getDay() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date();
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(0);
        endDate = customEndDate ? new Date(customEndDate + 'T23:59:59') : new Date();
        break;
      default:
        startDate = new Date(0);
        endDate = new Date();
    }
    return { startDate, endDate };
  };

  const fetchDriversWithEarnings = async () => {
    setLoading(true);
    setError(null);

    try {
      let driversQuery = supabase.from('drivers').select('*').order('name');

      if (driverFilter === 'active') {
        driversQuery = driversQuery.eq('status', 'active');
      } else if (driverFilter === 'inactive') {
        driversQuery = driversQuery.neq('status', 'active');
      }

      const { data: driversData, error: driversError } = await driversQuery;
      if (driversError) throw driversError;

      const { startDate, endDate } = getDateRange();

      const { data: assignments, error: assignError } = await supabase
        .from('driver_assignments')
        .select('driver_id, payment_status')
        .eq('status', 'delivered')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (assignError) throw assignError;

      const earningsMap = {};
      (assignments || []).forEach(a => {
        if (!earningsMap[a.driver_id]) {
          earningsMap[a.driver_id] = { total: 0, unpaid: 0, approved: 0, paid: 0 };
        }
        earningsMap[a.driver_id].total += 1;
        const status = a.payment_status || 'unpaid';
        if (status === 'paid') earningsMap[a.driver_id].paid += 1;
        else if (status === 'approved') earningsMap[a.driver_id].approved += 1;
        else earningsMap[a.driver_id].unpaid += 1;
      });

      const driversWithEarnings = (driversData || []).map(driver => {
        // Try both driver.id and driver.user_id as the key since driver_assignments.driver_id could reference either
        const earnings = earningsMap[driver.id] || earningsMap[driver.user_id] || { total: 0, unpaid: 0, approved: 0, paid: 0 };
        return {
          ...driver,
          // Store the matched key so expanded view uses the correct one
          _assignmentDriverId: earningsMap[driver.id] ? driver.id : driver.user_id,
          deliveries: earnings.total,
          totalEarned: earnings.total * EARNING_PER_DELIVERY,
          unpaidCount: earnings.unpaid,
          unpaidAmount: earnings.unpaid * EARNING_PER_DELIVERY,
          approvedCount: earnings.approved,
          approvedAmount: earnings.approved * EARNING_PER_DELIVERY,
          paidCount: earnings.paid,
          paidAmount: earnings.paid * EARNING_PER_DELIVERY
        };
      });

      // Sort: drivers with deliveries first, then by name
      driversWithEarnings.sort((a, b) => {
        if (b.deliveries !== a.deliveries) return b.deliveries - a.deliveries;
        return (a.name || '').localeCompare(b.name || '');
      });

      setDrivers(driversWithEarnings);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDeliveries = async (driver) => {
    setLoadingDeliveries(true);
    setSelectedDeliveries([]);

    try {
      const { startDate, endDate } = getDateRange();

      let query = supabase
        .from('driver_assignments')
        .select('id, order_id, created_at, payment_status, payment_approved_at, payment_paid_at, orders (order_number, total, delivery_zone)')
        .eq('driver_id', driver._assignmentDriverId || driver.user_id || driver.id)
        .eq('status', 'delivered')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (paymentStatusFilter !== 'all') {
        if (paymentStatusFilter === 'unpaid') {
          query = query.or('payment_status.is.null,payment_status.eq.unpaid');
        } else {
          query = query.eq('payment_status', paymentStatusFilter);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      setDriverDeliveries(data || []);
    } catch (err) {
      console.error('Error fetching deliveries:', err);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleExpandDriver = async (driver) => {
    if (expandedDriver?.id === driver.id) {
      setExpandedDriver(null);
      setDriverDeliveries([]);
    } else {
      setExpandedDriver(driver);
      await fetchDriverDeliveries(driver);
    }
  };

  const handleApprovePayment = async (assignmentIds) => {
    if (!assignmentIds.length) return;
    const driverId = expandedDriver?.id || expandedDriver?.user_id;
    setProcessing(driverId);
    try {
      const { data, error, count } = await supabase
        .from('driver_assignments')
        .update({
          payment_status: 'approved',
          payment_approved_at: new Date().toISOString(),
          payment_approved_by: (await supabase.auth.getUser()).data.user?.id
        })
        .in('id', assignmentIds)
        .select();
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        alert('Update failed — you may not have permission. Please check your admin role.');
        return;
      }
      
      await fetchDriversWithEarnings();
      if (expandedDriver) await fetchDriverDeliveries(expandedDriver);
      setSelectedDeliveries([]);
    } catch (err) {
      alert('Error approving payment: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkAsPaid = async (assignmentIds) => {
    if (!assignmentIds.length) return;
    const driverId = expandedDriver?.id || expandedDriver?.user_id;
    setProcessing(driverId);
    try {
      const { data, error } = await supabase
        .from('driver_assignments')
        .update({
          payment_status: 'paid',
          payment_paid_at: new Date().toISOString(),
          payment_paid_by: (await supabase.auth.getUser()).data.user?.id
        })
        .in('id', assignmentIds)
        .select();
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        alert('Update failed — you may not have permission. Please check your admin role.');
        return;
      }
      
      await fetchDriversWithEarnings();
      if (expandedDriver) await fetchDriverDeliveries(expandedDriver);
      setSelectedDeliveries([]);
    } catch (err) {
      alert('Error marking as paid: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedDeliveries.length === 0) return alert('Please select deliveries first');
    const amount = selectedDeliveries.length * EARNING_PER_DELIVERY;
    if (window.confirm(`${action === 'approve' ? 'Approve' : 'Mark as paid'} ${selectedDeliveries.length} deliveries (R${amount.toFixed(2)})?`)) {
      action === 'approve' ? await handleApprovePayment(selectedDeliveries) : await handleMarkAsPaid(selectedDeliveries);
    }
  };

  const handleApproveAllUnpaid = async (driver) => {
    const ids = driverDeliveries.filter(d => !d.payment_status || d.payment_status === 'unpaid').map(d => d.id);
    if (!ids.length) return alert('No unpaid deliveries');
    if (window.confirm(`Approve ${ids.length} deliveries (R${(ids.length * EARNING_PER_DELIVERY).toFixed(2)}) for ${driver.name}?`)) {
      await handleApprovePayment(ids);
    }
  };

  const handleMarkAllApprovedAsPaid = async (driver) => {
    const ids = driverDeliveries.filter(d => d.payment_status === 'approved').map(d => d.id);
    if (!ids.length) return alert('No approved deliveries');
    if (window.confirm(`Mark ${ids.length} deliveries (R${(ids.length * EARNING_PER_DELIVERY).toFixed(2)}) as PAID for ${driver.name}?`)) {
      await handleMarkAsPaid(ids);
    }
  };

  const toggleDeliverySelection = (id) => {
    setSelectedDeliveries(prev => prev.includes(id) ? prev.filter(did => did !== id) : [...prev, id]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => `R${amount.toFixed(2)}`;

  const getStatusBadge = (status) => {
    const s = status || 'unpaid';
    if (s === 'paid') return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">✓ Paid</span>;
    if (s === 'approved') return <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">⏳ Approved</span>;
    return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">○ Unpaid</span>;
  };

  const totals = drivers.reduce((acc, d) => ({
    deliveries: acc.deliveries + d.deliveries,
    earned: acc.earned + d.totalEarned,
    unpaid: acc.unpaid + d.unpaidAmount,
    approved: acc.approved + d.approvedAmount,
    paid: acc.paid + d.paidAmount
  }), { deliveries: 0, earned: 0, unpaid: 0, approved: 0, paid: 0 });

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading driver payments...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Driver Payments</h2>
        <span className="text-sm text-gray-500">R{EARNING_PER_DELIVERY} per delivery</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 uppercase">Deliveries</p>
          <p className="text-2xl font-bold text-gray-900">{totals.deliveries}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 uppercase">Total Earned</p>
          <p className="text-2xl font-bold text-gray-600">{formatCurrency(totals.earned)}</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 shadow-sm border border-orange-200">
          <p className="text-xs text-orange-600 uppercase">Unpaid</p>
          <p className="text-2xl font-bold text-orange-600">{formatCurrency(totals.unpaid)}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-200">
          <p className="text-xs text-blue-600 uppercase">Approved</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.approved)}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-200">
          <p className="text-xs text-green-600 uppercase">Paid</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.paid)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 w-20">Drivers:</span>
          <div className="flex gap-2">
            {[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'all', label: 'All' }].map(f => (
              <button key={f.value} onClick={() => setDriverFilter(f.value)} className={`px-3 py-1.5 text-sm rounded-lg ${driverFilter === f.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{f.label}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
          <span className="text-sm font-medium text-gray-700 w-20">Status:</span>
          <div className="flex gap-2">
            {[{ value: 'all', label: 'All' }, { value: 'unpaid', label: '○ Unpaid' }, { value: 'approved', label: '⏳ Approved' }, { value: 'paid', label: '✓ Paid' }].map(f => (
              <button key={f.value} onClick={() => setPaymentStatusFilter(f.value)} className={`px-3 py-1.5 text-sm rounded-lg ${paymentStatusFilter === f.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{f.label}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
          <span className="text-sm font-medium text-gray-700 w-20">Period:</span>
          <div className="flex flex-wrap gap-2">
            {[{ value: 'today', label: 'Today' }, { value: 'yesterday', label: 'Yesterday' }, { value: 'this_week', label: 'This Week' }, { value: 'last_week', label: 'Last Week' }, { value: 'this_month', label: 'This Month' }, { value: 'last_month', label: 'Last Month' }].map(f => (
              <button key={f.value} onClick={() => { setDateFilter(f.value); setShowDatePicker(false); }} className={`px-3 py-1.5 text-sm rounded-lg ${dateFilter === f.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{f.label}</button>
            ))}
            <button onClick={() => { setDateFilter('custom'); setShowDatePicker(true); }} className={`px-3 py-1.5 text-sm rounded-lg ${dateFilter === 'custom' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Custom</button>
          </div>
        </div>

        {showDatePicker && dateFilter === 'custom' && (
          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <p className="text-xs text-gray-600 text-center">
          <span className="inline-flex items-center gap-1 mr-3"><span className="w-2 h-2 rounded-full bg-orange-400"></span> Unpaid</span>
          <span className="mx-1">→</span>
          <span className="inline-flex items-center gap-1 mx-3"><span className="w-2 h-2 rounded-full bg-blue-400"></span> Approved (Queued)</span>
          <span className="mx-1">→</span>
          <span className="inline-flex items-center gap-1 ml-3"><span className="w-2 h-2 rounded-full bg-green-400"></span> Paid (EFT Done)</span>
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Drivers ({drivers.length})</h3>
        </div>

        {error ? (
          <div className="p-8 text-center">
            <p className="text-red-500">Error: {error}</p>
            <button onClick={fetchDriversWithEarnings} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
          </div>
        ) : drivers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">👷</div>
            <p className="text-gray-500">No drivers found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {drivers.map((driver) => (
              <div key={driver.id}>
                <div className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${expandedDriver?.id === driver.id ? 'bg-blue-50' : ''}`} onClick={() => handleExpandDriver(driver)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold">{driver.name?.charAt(0)?.toUpperCase() || '?'}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{driver.name}</p>
                        <p className="text-sm text-gray-500">{driver.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center hidden sm:block">
                        <p className="text-xs text-gray-500">Deliveries</p>
                        <p className="font-bold text-gray-900">{driver.deliveries}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-orange-500">Unpaid</p>
                        <p className="font-bold text-orange-600">{formatCurrency(driver.unpaidAmount)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-blue-500">Approved</p>
                        <p className="font-bold text-blue-600">{formatCurrency(driver.approvedAmount)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-green-500">Paid</p>
                        <p className="font-bold text-green-600">{formatCurrency(driver.paidAmount)}</p>
                      </div>
                      <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedDriver?.id === driver.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {expandedDriver?.id === driver.id && (
                  <div className="bg-gray-50 border-t border-gray-200 p-4">
                    {loadingDeliveries ? (
                      <div className="text-center py-4">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : driverDeliveries.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">No deliveries in this period</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center gap-4 text-sm">
                            {driver.unpaidCount > 0 && <span className="text-orange-600">{driver.unpaidCount} unpaid ({formatCurrency(driver.unpaidAmount)})</span>}
                            {driver.approvedCount > 0 && <span className="text-blue-600">{driver.approvedCount} approved ({formatCurrency(driver.approvedAmount)})</span>}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedDeliveries.length > 0 && (
                              <>
                                <button onClick={() => handleBulkAction('approve')} disabled={processing === driver.id} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Approve Selected ({selectedDeliveries.length})</button>
                                <button onClick={() => handleBulkAction('paid')} disabled={processing === driver.id} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">Mark Paid ({selectedDeliveries.length})</button>
                              </>
                            )}
                            {driver.unpaidCount > 0 && <button onClick={() => handleApproveAllUnpaid(driver)} disabled={processing === driver.id} className="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-50">{processing === driver.id ? 'Processing...' : 'Approve All Unpaid'}</button>}
                            {driver.approvedCount > 0 && <button onClick={() => handleMarkAllApprovedAsPaid(driver)} disabled={processing === driver.id} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">{processing === driver.id ? 'Processing...' : 'Mark All as Paid'}</button>}
                          </div>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b border-gray-200">
                                <th className="pb-2 pr-4 w-8"></th>
                                <th className="pb-2 pr-4">Order</th>
                                <th className="pb-2 pr-4">Date</th>
                                <th className="pb-2 pr-4">Zone</th>
                                <th className="pb-2 pr-4">Earning</th>
                                <th className="pb-2 pr-4">Status</th>
                                <th className="pb-2 text-right">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {driverDeliveries.map((delivery) => {
                                const status = delivery.payment_status || 'unpaid';
                                return (
                                  <tr key={delivery.id} className="hover:bg-white">
                                    <td className="py-2 pr-4">
                                      {status !== 'paid' && <input type="checkbox" checked={selectedDeliveries.includes(delivery.id)} onChange={() => toggleDeliverySelection(delivery.id)} className="w-4 h-4 rounded border-gray-300 text-blue-600" />}
                                    </td>
                                    <td className="py-2 pr-4 font-medium">#{delivery.orders?.order_number || 'N/A'}</td>
                                    <td className="py-2 pr-4 text-gray-600">{formatDate(delivery.created_at)}</td>
                                    <td className="py-2 pr-4 text-gray-600">{delivery.orders?.delivery_zone || '-'}</td>
                                    <td className="py-2 pr-4 font-medium">{formatCurrency(EARNING_PER_DELIVERY)}</td>
                                    <td className="py-2 pr-4">{getStatusBadge(status)}</td>
                                    <td className="py-2 text-right">
                                      {status === 'unpaid' && <button onClick={() => { if (window.confirm(`Approve R${EARNING_PER_DELIVERY} for #${delivery.orders?.order_number}?`)) handleApprovePayment([delivery.id]); }} disabled={processing === driver.id} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Approve</button>}
                                      {status === 'approved' && <button onClick={() => { if (window.confirm(`Mark R${EARNING_PER_DELIVERY} as PAID for #${delivery.orders?.order_number}?`)) handleMarkAsPaid([delivery.id]); }} disabled={processing === driver.id} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">Mark Paid</button>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDriverPayments;
